<template>
  <v-card flat>
    <v-row align="center">
      <v-btn
        @click="togglePlay"
        icon
        @keydown.space.prevent="togglePlay"
        @keydown.enter.prevent="togglePlay"
      >
        <v-icon>{{ isPlaying ? 'mdi-pause' : 'mdi-play' }}</v-icon>
      </v-btn>

      <v-slider
        v-model="progress"
        class="mx-2"
        :color="isDark ? 'grey lighten-1' : 'grey darken-1'"
        :track-color="isDark ? 'grey darken-2' : 'grey lighten-2'"
        hide-details
        @input="seek"
        style="flex: 1"
      />

      <span class="mx-2 time-text">{{ formattedTime }}</span>

      <!-- Volume button with overlay -->
      <v-menu
        v-model="showVolumeSlider"
        offset-y
        open-on-click
        :close-on-content-click="false"
        transition="fade-transition"
        :nudge-left="100"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on">
            <v-icon>mdi-volume-high</v-icon>
          </v-btn>
        </template>

        <v-card flat class="pa-4">
          <v-slider
            v-model="volume"
            :label="volumePourcentage"
            inverse-label
            min="0"
            max="1"
            step="0.01"
            :color="isDark ? 'grey lighten-1' : 'grey darken-1'"
            :track-color="isDark ? 'grey darken-2' : 'grey lighten-2'"
            hide-details
            style="width: 150px;"
            @input="setVolume"
          />
        </v-card>
      </v-menu>

      <v-btn
        icon
        :href="src"
        download
        target="_blank"
        class="ml-2"
      >
        <v-icon>mdi-download</v-icon>
      </v-btn>
    </v-row>
  </v-card>
</template>

<script>
export default {
  name: 'voicemail-audio-player',
  props: {
    src: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      audio: null,
      isPlaying: false,
      progress: 0,
      volume: 1,
      showVolumeSlider: false,
      currentTime: 0
    }
  },
  computed: {
    isDark () {
      return this.$vuetify.theme.dark
    },
    formattedTime () {
      if (this.currentTime) {
        const current = Math.floor(this.currentTime)
        const minutes = Math.floor(current / 60)
        const seconds = current % 60
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
      } else {
        return '0:00'
      }
    },
    volumePourcentage () {
      return Math.floor((this.volume * 100).toFixed(2)) + '%'
    }
  },
  mounted () {
    this.initAudio()
    this.$root.$on('pause-audio', () => {
      this.pauseAudio()
    })
  },
  beforeDestroy () {
    if (this.audio) {
      this.audio.removeEventListener('timeupdate', this.updateProgress)
      this.audio.removeEventListener('ended', this.onEnded)
      this.audio.pause()
    }
  },
  methods: {
    initAudio () {
      if (this.audio) {
        this.audio.pause()
        this.audio.removeEventListener('timeupdate', this.updateProgress)
        this.audio.removeEventListener('ended', this.onEnded)
      }

      this.audio = new Audio(this.src)
      this.audio.volume = this.volume

      this.audio.addEventListener('timeupdate', this.updateProgress)
      this.audio.addEventListener('ended', this.onEnded)
    },
    togglePlay () {
      if (!this.audio) return

      if (this.audio.paused) {
        this.audio.play().then(() => {
          this.isPlaying = true
        })
      } else {
        this.audio.pause()
        this.isPlaying = false
      }
    },
    pauseAudio () {
      if (!this.audio) return
      this.audio.pause()
      this.isPlaying = false
    },
    updateProgress () {
      if (this.audio && this.audio.duration) {
        this.currentTime = this.audio.currentTime
        this.progress = Math.round(this.audio.currentTime / this.audio.duration * 100)
      }
    },
    seek (value) {
      if (this.audio && this.audio.duration) {
        this.audio.currentTime = (value / 100) * this.audio.duration
      }
    },
    setVolume () {
      if (this.audio) {
        this.audio.volume = this.volume
      }
    },
    onEnded () {
      this.isPlaying = false
      this.progress = 0
      this.currentTime = 0
    }
  }
}
</script>

<style scoped>
.time-text {
  min-width: 35px;
  text-align: center;
  font-size: 14px;
}

.volume-container {
  position: relative;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
